// Invoices -> Reconcile with QuickBooks.
//
// The owner could not find an invoice (Mertler #361) that QuickBooks had, and
// the year's totals were $10k apart. This puts both lists side by side for a
// date range: the totals pre-tax and incl. tax, what makes up the gap, and
// every invoice that is on one side only. READ-ONLY -- the server only runs
// SELECTs against QuickBooks (quickbooks/reconcile.py).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiErrorMessage } from "../../api";
import LoadingSpinner from "../../components/LoadingSpinner";
import { isManager } from "./StatusPill";
import { rangeFor, todayIsoLocal } from "./dateRanges";
import { reconcileCsv } from "./reconcileCsv";

const money = (v) => {
  const n = Number(v || 0);
  const s = Math.abs(n).toLocaleString("en-CA", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return `${n < 0 ? "-" : ""}$${s}`;
};

const longDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-CA", {
    day: "numeric", month: "short", year: "numeric", timeZone: "UTC",
  });
};

const HOW_LABEL = { qbo_id: "QuickBooks link", number: "Invoice number", probable: "Probable" };

const TABS = [
  { key: "only_in_quickbooks", label: "Only in QuickBooks" },
  { key: "only_in_clientzone", label: "Only in ClientZone" },
  { key: "differences", label: "Amount differences" },
  { key: "drafts", label: "Drafts in range" },
  { key: "credit_memos", label: "QuickBooks credit memos" },
  { key: "matched", label: "Matched" },
];

const th = "px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase";
const thR = "px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase";
const td = "px-3 py-2 text-sm text-gray-800";
const tdR = "px-3 py-2 text-sm text-gray-800 text-right tabular-nums";

function CzLink({ row }) {
  return <Link className="text-blue-700 hover:underline" to={`/invoices/${row.id}`}>{row.number}</Link>;
}

function QboRows({ rows, showBalance = true }) {
  return (
    <table className="min-w-full">
      <thead className="bg-gray-50"><tr>
        <th className={th}>QBO #</th><th className={th}>Date</th><th className={th}>Customer</th>
        <th className={thR}>Pre-tax</th><th className={thR}>Tax</th><th className={thR}>Total</th>
        {showBalance && <th className={thR}>Balance</th>}<th className={th}>Note</th>
      </tr></thead>
      <tbody className="divide-y">
        {rows.map((q) => (
          <tr key={q.qbo_id}>
            <td className={td}>{q.doc_number || "(none)"}</td><td className={td}>{q.date}</td>
            <td className={td}>{q.customer_name}</td>
            <td className={tdR}>{money(q.pretax)}</td><td className={tdR}>{money(q.tax)}</td>
            <td className={tdR}>{money(q.total)}</td>
            {showBalance && <td className={tdR}>{money(q.balance)}</td>}
            <td className={`${td} text-gray-600`}>{q.note}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CzRows({ rows }) {
  return (
    <table className="min-w-full">
      <thead className="bg-gray-50"><tr>
        <th className={th}>Invoice</th><th className={th}>Date</th><th className={th}>Client</th>
        <th className={th}>Status</th><th className={thR}>Pre-tax</th><th className={thR}>Tax</th>
        <th className={thR}>Total</th><th className={th}>Note</th>
      </tr></thead>
      <tbody className="divide-y">
        {rows.map((c) => (
          <tr key={c.id}>
            <td className={td}><CzLink row={c} /></td><td className={td}>{c.date}</td>
            <td className={td}>{c.client_name}</td><td className={td}>{c.status}</td>
            <td className={tdR}>{money(c.pretax)}</td><td className={tdR}>{money(c.tax)}</td>
            <td className={tdR}>{money(c.total)}</td>
            <td className={`${td} text-gray-600`}>{c.note}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MatchedRows({ rows }) {
  return (
    <table className="min-w-full">
      <thead className="bg-gray-50"><tr>
        <th className={th}>Invoice</th><th className={th}>QBO #</th><th className={th}>Date</th>
        <th className={th}>Client</th><th className={thR}>ClientZone</th>
        <th className={thR}>QuickBooks</th><th className={thR}>Difference</th>
        <th className={th}>Matched by</th>
      </tr></thead>
      <tbody className="divide-y">
        {rows.map((m) => (
          <tr key={`${m.cz.id}-${m.qbo.qbo_id}`}>
            <td className={td}><CzLink row={m.cz} /></td>
            <td className={td}>{m.qbo.doc_number}</td>
            <td className={td}>
              {m.cz.date}{m.date_differs ? ` (QBO ${m.qbo.date})` : ""}
            </td>
            <td className={td}>{m.cz.client_name}</td>
            <td className={tdR}>{money(m.cz.total)}</td><td className={tdR}>{money(m.qbo.total)}</td>
            <td className={tdR}>{money(m.diff_total)}</td>
            <td className={td}>{HOW_LABEL[m.how] || m.how}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TotalsTable({ report }) {
  const t = report.totals;
  const row = (label, s, opts = {}) => (
    <tr className={opts.strong ? "font-semibold" : opts.muted ? "text-gray-500" : ""}>
      <td className={td}>{label}</td>
      <td className={tdR}>{s.count ?? ""}</td>
      <td className={tdR}>{money(s.pretax)}</td>
      <td className={tdR}>{money(s.tax)}</td>
      <td className={tdR}>{money(s.total)}</td>
    </tr>
  );
  return (
    <table aria-label="Reconciliation totals" className="min-w-full bg-white rounded border">
      <thead className="bg-gray-50"><tr>
        <th className={th}></th><th className={thR}>Invoices</th><th className={thR}>Pre-tax</th>
        <th className={thR}>Tax</th><th className={thR}>Incl. tax</th>
      </tr></thead>
      <tbody className="divide-y">
        {row("ClientZone", t.clientzone)}
        {row("QuickBooks", t.quickbooks)}
        {row("Difference", t.difference, { strong: true })}
        {row("Drafts in ClientZone (not in totals above)", t.drafts, { muted: true })}
        {row("Credit memos in QuickBooks (not in totals above)", t.credit_memos, { muted: true })}
      </tbody>
    </table>
  );
}

export default function ReconcilePage({ user }) {
  const allowed = Boolean(isManager(user) || user?.can_finalize_invoices || user?.is_superuser);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [client, setClient] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("only_in_quickbooks");
  const started = useRef(false);

  const run = useCallback(async (start, end, name) => {
    setLoading(true);
    setErr("");
    try {
      const qs = new URLSearchParams({ from: start, to: end });
      if (name && name.trim()) qs.set("client", name.trim());
      setReport(await apiGet(`/api/quickbooks/invoices/reconcile/?${qs.toString()}`));
    } catch (e) {
      setReport(null);
      setErr(apiErrorMessage(e, "Could not reconcile with QuickBooks."));
    } finally {
      setLoading(false);
    }
  }, []);

  // Default: the last completed firm fiscal year, then run once.
  useEffect(() => {
    if (!allowed || started.current) return;
    started.current = true;
    apiGet("/api/firm-settings/")
      .then((s) => s?.fiscal_year_end_month ?? 6)
      .catch(() => 6)
      .then((month) => {
        const r = rangeFor("last_fiscal_year", todayIsoLocal(), month);
        setFrom(r.start);
        setTo(r.end);
        run(r.start, r.end, "");
      });
  }, [allowed, run]);

  const exportCsv = () => {
    const blob = new Blob([reconcileCsv(report)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `quickbooks-reconcile-${report.start}-to-${report.end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!allowed) {
    return (
      <div className="p-6">
        <p className="text-gray-700">
          Only managers or people who can finalise invoices can reconcile with QuickBooks.
        </p>
      </div>
    );
  }

  const rowsFor = (key) => (report ? report[key] || [] : []);

  return (
    <div className="w-full bg-gray-50 min-h-screen pb-10">
      <div className="px-6 py-4 border-b bg-white flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-800">Reconcile with QuickBooks</h1>
        <Link to="/invoices" className="text-sm text-blue-700 hover:underline">Back to invoices</Link>
      </div>

      <div className="p-6 space-y-4">
        <form
          className="bg-white rounded border p-4 flex flex-wrap items-end gap-4"
          onSubmit={(e) => { e.preventDefault(); run(from, to, client); }}
        >
          <label className="text-sm text-gray-700 flex flex-col">
            From
            <input type="date" className="border rounded px-2 py-1" value={from}
                   onChange={(e) => setFrom(e.target.value)} required />
          </label>
          <label className="text-sm text-gray-700 flex flex-col">
            To
            <input type="date" className="border rounded px-2 py-1" value={to}
                   onChange={(e) => setTo(e.target.value)} required />
          </label>
          <label className="text-sm text-gray-700 flex flex-col">
            Client name contains (optional)
            <input type="text" className="border rounded px-2 py-1" value={client}
                   onChange={(e) => setClient(e.target.value)} />
          </label>
          <button type="submit" disabled={loading}
                  className="rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                  style={{ background: "#007E33" }}>
            Run
          </button>
          {report && (
            <button type="button" onClick={exportCsv}
                    className="rounded-md px-4 py-2 text-sm font-medium border bg-white">
              Export CSV
            </button>
          )}
          <p className="w-full text-xs text-gray-500">
            Invoices dated {longDate(from)} to {longDate(to)} inclusive, by invoice date on
            each side. Read-only: nothing in QuickBooks or ClientZone is changed.
          </p>
        </form>

        {err && (
          <div role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {err}
          </div>
        )}

        {loading && <LoadingSpinner text="Reading QuickBooks..." size={22} className="py-10" />}

        {report && !loading && (
          <>
            <h2 className="text-lg font-semibold text-gray-800">
              {longDate(report.start)} to {longDate(report.end)}
              {report.client ? ` - clients matching "${report.client}"` : ""}
            </h2>
            <TotalsTable report={report} />

            <div className="bg-white rounded border p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Gap explained by</h3>
              <ul className="text-sm text-gray-700 space-y-1 font-mono whitespace-pre-wrap">
                {(report.gap_explained || []).map((line, i) => <li key={i}>{line}</li>)}
              </ul>
            </div>

            <div className="bg-white rounded border">
              <div role="tablist" className="flex flex-wrap border-b">
                {TABS.map((t) => (
                  <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
                          onClick={() => setTab(t.key)}
                          className={`px-4 py-2 text-sm ${tab === t.key
                            ? "border-b-2 border-green-700 font-semibold text-gray-900"
                            : "text-gray-600"}`}>
                    {t.label} ({rowsFor(t.key).length})
                  </button>
                ))}
              </div>
              <div role="tabpanel" className="overflow-x-auto">
                {rowsFor(tab).length === 0 ? (
                  <p className="p-4 text-sm text-gray-500">Nothing here.</p>
                ) : tab === "only_in_quickbooks" ? (
                  <QboRows rows={rowsFor(tab)} />
                ) : tab === "credit_memos" ? (
                  <QboRows rows={rowsFor(tab)} showBalance={false} />
                ) : tab === "differences" || tab === "matched" ? (
                  <MatchedRows rows={rowsFor(tab)} />
                ) : (
                  <CzRows rows={rowsFor(tab)} />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
